// src/components/reports/StaffSelector.js

import React from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { useReportContext } from '../../contexts/ReportContext';

/**
 * Component for selecting staff members to include in reports
 */
const StaffSelector = () => {
  const { t } = useTranslation(['reports', 'staff']);
  const { 
    staffMembers, 
    selectedStaffIds, 
    isSelectingAll,
    toggleSelectAll, 
    toggleStaffSelection 
  } = useReportContext();
  
  // Convert staff members to react-select options format
  // Archived staff remain selectable (reports must keep historical data),
  // but are labelled and sorted after active staff.
  const options = staffMembers
    .map(staff => {
      const isArchived = staff.active === false;
      return {
        value: staff.id,
        label: isArchived
          ? `${staff.name} (${t('archive.archived', { ns: 'staff' })})`
          : staff.name,
        isArchived
      };
    })
    .sort((a, b) =>
      a.isArchived === b.isArchived
        ? a.label.localeCompare(b.label)
        : (a.isArchived ? 1 : -1)
    );
  
  // Get currently selected options for the dropdown value
  const selectedOptions = options.filter(option => 
    selectedStaffIds.has(option.value)
  );
  
  // Handle multi-select changes
  const handleSelectChange = (selectedOptions) => {
    // Get the newly selected staff IDs
    const newSelectedIds = new Set(selectedOptions ? selectedOptions.map(option => option.value) : []);
    
    // For each staff member, toggle if needed to match the new selection
    staffMembers.forEach(staff => {
      const isCurrentlySelected = selectedStaffIds.has(staff.id);
      const shouldBeSelected = newSelectedIds.has(staff.id);
      
      if (isCurrentlySelected !== shouldBeSelected) {
        toggleStaffSelection(staff.id);
      }
    });
  };
  
  if (staffMembers.length === 0) {
    return (
      <div className="staff-selector">
        <div className="staff-selector-title">{t('staffMembersLabel')}</div>
        <div className="staff-selector-empty">
          {t('noStaffMembersFound')}
        </div>
      </div>
    );
  }
  
  return (
    <div className="staff-selector">
      <div className="staff-selector-header">
        <div className="staff-selector-title">{t('staffMembersLabel')}</div>
        
        <button 
          type="button" 
          className="select-all-button"
          onClick={toggleSelectAll}
        >
          {isSelectingAll ? t('deselectAll') : t('selectAll')}
        </button>
      </div>
      
      <div className="staff-dropdown-container">
        <Select
          isMulti
          name="staff-members"
          options={options}
          value={selectedOptions}
          onChange={handleSelectChange}
          className="staff-multi-select"
          classNamePrefix="staff-select"
          placeholder={t('searchStaffPlaceholder')}
          isClearable={false}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
        />
      </div>
      
      {/* Add some helpful text */}
      <div className="staff-select-help">
        {selectedStaffIds.size === 0 ? (
          <span className="no-staff-warning">{t('pleaseSelectStaffMember')}</span>
        ) : (
          <span className="staff-count">{t('staffMemberSelected', { count: selectedStaffIds.size })}</span>
        )}
      </div>
    </div>
  );
};

export default StaffSelector;